<template>
  <div>
    <div class="document-container">
      <div class="columns">
        <div class="column is-three-quarters">
          <div class="document" style="height:100%">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Process Details
                  </div>
                  <div class="header-subtitle">
                    Displays details about processing.
                  </div>
                </template>
              </EsHeader>
            </div>
            <div class="content">
              <es-loader v-if="this.loading === true"/>
              <div v-else>
                <es-form>
                  <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon
                          :icon="['fas', 'chart-line']"
                          size="lg"
                        />
                      </div>
                      <div class="header-elements">
                        <h6 class="title">
                          Status Statistics
                        </h6>
                      </div>
                    </div>
                  </template>
                  <template #es-form>
                    <table class="table is-fullwidth" v-if="transaction.id">
                      <thead>
                        <tr>
                          <th class="column-header has-text-grey">
                            Match Status
                          </th>
                          <th class="text-align--right has-text-grey">
                            Record Count
                          </th>
                        </tr>
                      </thead>
                      <tbody v-if="transaction.dmTransStatus">
                        <tr>
                          <td
                            class ="has-text-weight-bold removeUnderline"
                            colspan="2"
                          >
                            {{ getCategory(transaction.dmTransStatus.code) }}
                          </td>
                        </tr>
                        <tr class="row-border">
                          <td
                            class ="has-text-grey removeUnderline"
                            style="padding-left: 1rem"
                          >
                            {{ transaction.dmTransStatus.description }}
                            <!-- Near Match system label needed -->
                            <!-- `${this.properties['SYSTEM_LABEL-NEARMATCH']}` -->
                          </td>
                          <td
                            class ="text-align--right removeUnderline"
                            style="padding-right: 1rem"
                          >
                            1
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <es-exception v-if="!transaction.dmTransStatus"
                      style="margin:2rem"
                      exceptionMessage="This transaction has no matches. There is no record to download."
                    />
                  </template>
                </es-form>
                <es-form style="padding-top: 0">
                  <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon :icon="['fas', 'clock']" size="lg"/>
                      </div>
                      <div class="header-elements">
                        <h6 class="title">
                          Processing Stage
                        </h6>
                      </div>
                    </div>
                  </template>
                  <template #es-form>
                    <table class="table is-fullwidth" v-if="transaction.id">
                      <thead>
                        <th class="column-header has-text-grey">
                          Stage
                        </th>
                        <th class="text-align--right has-text-grey">
                          Processing Date
                        </th>
                      </thead>
                      <tbody v-if="processingStageData">
                        <tr class="processing-stage"
                          v-for="(item,index)
                            in transaction.dmBatch.processingStages.stages"
                          :key="index"
                        >
                          <template v-for="(date,stage,i) in item">
                            <td class ="has-text-grey">
                              {{ stage }}
                              <!-- Near Match System Label needed -->
                            </td>
                            <td class="text-align--right">
                              {{ date | formatDateMMDDYYYYHHMM }}
                            </td>
                          </template>
                        </tr>
                      </tbody>
                    </table>
                    <es-exception
                      v-if="!processingStageData"
                      style="margin:2rem"
                      exceptionMessage="No processing stage is available."
                    />
                  </template>
                </es-form>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-one-quarter">
          <div class="document margin-bottom">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Next Steps
                  </div>
                </template>
              </EsHeader>
            </div>
            <div class="is-flex flex-direction--column flex-align--start"
              style="padding: 1rem"
            >
              <div class="margin-bottom--small">
                <router-link
                  v-if="hasModule('DRMCH:MATCH:RESOLVENM:VIEW')
                    && ['2'].includes(this.transaction.dmTransStatus.code)"
                  :to="{
                    path: '/directmatch/nearMatches',
                    query: { dmMatchTransId: this.transaction.id},
                  }"
                  class="button is-primary has-no-underline"
                >
                  Resolve
                </router-link>
              </div>
              <div class="margin-bottom--small">
                <router-link
                  v-if="hasModule('DRMCH:DWNLD:DOWNLOAD')"
                  :to="{
                      path: `/directmatch/transactions/${this.transaction.id}/download`,
                    }"
                  class="button is-primary has-no-underline"
                >
                  Download
                </router-link>
              </div>
              <div>
                <router-link
                  v-if="hasModule('DRMCH:MATCH:HISTORY:VIEW')
                    && transaction.dmBatchId"
                  :to="{
                      path: `/directMatch/batch/${transaction.dmBatchId}/details`
                    }"
                >
                  View Batch Profile
                </router-link>
              </div>
            </div>
          </div>
          <div class="document">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    General Information
                  </div>
                </template>
              </EsHeader>
            </div>
            <div class="content">
              <es-loader v-if="this.loading === true"/>
              <div v-else>
                <table
                  class="form-table table is-fullwidth"
                  v-if="transaction.id"
                >
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        Match Type
                      </label>
                    </td>
                    <td class= "text-align--right">
                      {{ transaction.program.description }}
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        {{ getFieldLabel(`PER:${defaultPersonType}:DIRECTMATCH:DISTRICT_CODE`) }}
                      </label>
                    </td>
                    <td class= "text-align--right">
                      <div v-if="transaction.district">
                        {{ transaction.district.location.name }} ( {{ transaction.district.code }} )
                      </div>
                      <div v-else>
                        N/A
                      </div>
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        {{ getFieldLabel(`PER:${defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`) }}
                      </label>
                    </td>
                    <td class= "text-align--right">
                      <div v-if="transaction.school">
                        {{ transaction.school.location.name }} ( {{ transaction.school.code }} )
                      </div>
                      <div v-else>
                        N/A
                      </div>
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        Extract Date
                      </label>
                    </td>
                    <td class= "text-align--right">
                      <div v-if="transaction.dmBatch">
                        {{ transaction.dmBatch.extractDate | formatDateMMDDYYYY }}
                      </div>
                      <div v-else>
                        N/A
                      </div>
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        Extract File
                      </label>
                    </td>
                    <td class= "text-align--right max-width">
                      {{ transaction.profileHeader.fileName || 'N/A' }}
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        Transmission ID
                      </label>
                    </td>
                    <td class= "text-align--right">
                      N/A
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        {{ getFieldLabel(`PER:${defaultPersonType}:DIRECTMATCH:SOURCE_CODE`) }}
                      </label>
                    </td>
                    <td
                      v-if="transaction.sourceCode"
                      class= "text-align--right"
                    >
                      {{transaction.sourceCode}}
                    </td>
                    <td v-else class= "text-align--right">
                      N/A
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        Creation Date
                      </label>
                    </td>
                    <td class= "text-align--right">
                      {{transaction.transDate | formatDateMMDDYYYYHHMM }}
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        Creation User
                      </label>
                    </td>
                    <td class= "text-align--right">
                      <div v-if="transaction.user">
                        {{transaction.user.email}}
                      </div>
                      <div v-else>
                        N/A
                      </div>
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        Delimiter (Record)
                      </label>
                    </td>
                    <td class= "text-align--right">
                      <span v-if="transaction.fileDetails">
                        {{
                          transaction.fileDetails.recordDelimiter
                          ? transaction.fileDetails.recordDelimiter
                          : 'N/A'
                        }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left">
                      <label class="label">
                        Delimiter (Header)
                      </label>
                    </td>
                    <td class= "text-align--right">
                      <span v-if="transaction.fileDetails">
                        {{
                          transaction.fileDetails.headerDelimiter
                          ? transaction.fileDetails.headerDelimiter
                          : 'N/A'
                        }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class= "form-row">
                    <td class= "text-align--left removeUnderline">
                      <label class="label">
                        File Version
                      </label>
                    </td>
                    <td class= "text-align--right removeUnderline">
                      <span v-if="transaction.fileDetails">
                        {{
                          transaction.fileDetails.fileVersion
                          ? transaction.fileDetails.fileVersion
                          : 'N/A'
                        }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>

                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faChartLine,
  faClock,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import { mapActions, mapGetters } from 'vuex';
import EsForm from '@/components/EsForm.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsException from '@/components/EsException.vue';
import moment from 'moment';
import PermissionManager from '@/mixins/PermissionManager';
import FieldLabels from '@/mixins/FieldLabels';

library.add(
  faChartLine,
  faClock,
  faExclamationTriangle,
);
export default {
  mixins: [
    PermissionManager,
    FieldLabels,
  ],
  components: {
    EsForm,
    EsHeader,
    EsException,
    EsLoader,
  },
  props:['transaction'],
  data() {
    return {
      //transaction: {},
      loading: false,
    };
  },
  async created() {
    await this.loadPersonTypes();
    //this.loadTransaction();
  },
  mounted() {
    this.getCategory();
  },
  computed: {
    ...mapGetters([
      'hasProgramPermission',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    processingStageData() {
      if (this.transaction.dmBatch.processingStages) {
        return this.transaction.dmBatch.processingStages.length;
      }
    }
  },
  methods: {
    ...mapActions([
      'getTransaction',
    ]),
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    toggleLoading() {
      this.loading = !this.loading;
    },
    getCategory(value) {
      let matchType = {};
      const matches = [1,14,16].forEach(code => matchType[code] = 'Match / No Match (Completed)');
      const noMatches = [4,15].forEach(code => matchType[code] = 'Match / No Match (Completed)');
      const canceled = [13, 17].forEach(code => matchType[code] = 'Canceled (Completed)');
      const nearMatch = [2].forEach(code => matchType[code] = `Ready to Resolve ${this.properties['SYSTEM_LABEL-NEARMATCH']}(s/es) (Pending)`);
      return matchType[value];
    },
    transactionPermission(permission) {
      return {
        permission,
        districtCode: this.transaction.districtCode,
        schoolCode: this.transaction.schoolCode,
        programCode: this.transaction.programCode,
      };
    },
  },
};
</script>

<style lang="sass" scoped>
  .text-align--right
    text-align: right !important
  .text-align--left
    text-align: left !important
  .removeUnderline
    border-bottom: 1px solid transparent !important
  .column-header
    padding-left: 0px
  .margin-bottom
    margin-bottom: 1rem
  .margin-bottom--small
    margin-bottom: 0.5rem
  .form-row
    line-height: 2rem
    padding-right: 0.5rem
  .row-border
    border-bottom: 1px solid #dee2e6
  .processing-stage:last-child
    border-bottom: none
  .max-width
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 20ch
</style>
